import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import MessageItem from "../chat/MessageItem";
import { mapDbMessagesToUi } from "../../services/ChatHistoryService";

const CHAT_SERVER_URL =
  process.env.REACT_APP_CHAT_SERVER_URL || "https://chatbotapi.pimath.id.vn";

/**
 * Public readonly page — hiển thị conversation đã share.
 * Không yêu cầu login. Không có input.
 */
function SharedConversation() {
  const { shareId } = useParams();
  const [state, setState] = useState({
    loading: true,
    error: null,
    data: null,
  });

  // Cho phép scroll document trong trang này (App.css global đang khóa html/body overflow cho Dashboard)
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");

    const prev = {
      htmlOverflow: html.style.overflow,
      htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow,
      bodyHeight: body.style.height,
      rootOverflow: root ? root.style.overflow : "",
      rootHeight: root ? root.style.height : "",
    };

    html.style.overflow = "auto";
    html.style.height = "auto";
    body.style.overflow = "auto";
    body.style.height = "auto";
    if (root) {
      root.style.overflow = "auto";
      root.style.height = "auto";
    }

    return () => {
      html.style.overflow = prev.htmlOverflow;
      html.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow;
      body.style.height = prev.bodyHeight;
      if (root) {
        root.style.overflow = prev.rootOverflow;
        root.style.height = prev.rootHeight;
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${CHAT_SERVER_URL}/api/share/${encodeURIComponent(shareId)}`,
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || `Lỗi ${res.status}`);
        }
        const json = await res.json();
        if (cancelled) return;
        setState({ loading: false, error: null, data: json.data });
      } catch (err) {
        if (cancelled) return;
        setState({ loading: false, error: err.message, data: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shareId]);

  const formatTime = (iso) =>
    new Date(iso).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

  if (state.loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-white">
        <div className="flex items-center gap-3 text-gray-500 text-sm">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-red-600 rounded-full animate-spin" />
          Đang tải cuộc trò chuyện…
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-white p-6 text-center">
        <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
          <svg
            className="w-8 h-8 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Không truy cập được
        </h2>
        <p className="text-gray-500 text-sm max-w-sm">{state.error}</p>
        <Link
          to="/login"
          className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800"
        >
          Về trang chủ
        </Link>
      </div>
    );
  }

  const { conversation, messages } = state.data || {};
  const uiMessages = mapDbMessagesToUi(messages || []);

  return (
    <div className="min-h-[100dvh] bg-white">
      {/* Header */}
      <header className="pim-header">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center text-white text-sm font-bold">
            π
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">
              {conversation?.title || "Chat đã chia sẻ"}
            </div>
            <div className="text-[11px] text-gray-500">
              ChatBot PIMATH · Chia sẻ công khai (chỉ đọc)
            </div>
          </div>
        </div>
        <div className="flex-1" />
        <Link
          to="/login"
          className="text-xs text-red-600 hover:underline font-medium"
        >
          Đăng nhập PIMATH →
        </Link>
      </header>

      <div className="pim-chat-container pt-6 pb-10">
        <div className="space-y-6">
          {uiMessages.map((m) => (
            <MessageItem
              key={m.id}
              message={m}
              formatTime={formatTime}
              renderUserMath
            />
          ))}
        </div>

        {uiMessages.length === 0 && (
          <p className="text-center text-gray-400 text-sm py-10">
            Cuộc trò chuyện chưa có tin nhắn.
          </p>
        )}
      </div>

      <footer className="border-t border-gray-200 py-4 text-center">
        <p className="text-[11.5px] text-gray-400">
          Nội dung chia sẻ công khai bởi ChatBot PIMATH
        </p>
      </footer>
    </div>
  );
}

export default SharedConversation;
